import { useDatoClient } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useMemo, useRef, useState } from 'react';

import type React from 'react';

const logger = LogManager.getLogger('hook:useSpeakerFormHeadshots');

interface HeadshotEntry {
  id: string;
  url: string;
}

interface SpeakerHeadshotInput {
  headshot?: string | null;
  headshots?: HeadshotEntry[];
}

/** One headshot in display order: saved already, or a picked file not yet uploaded. */
type HeadshotItem =
  | { kind: 'existing'; entry: HeadshotEntry; preview: string }
  | { kind: 'new'; file: File; preview: string };

export interface SpeakerFormHeadshots {
  /**
   * Preview URLs in order, saved and newly picked alike. The first is the
   * primary — the headshot nametags show (PLAT-12178).
   */
  allHeadshotPreviews: string[];
  /** Whether an upload is currently in progress */
  isUploadingHeadshot: boolean;
  /** Ref for the hidden file input element */
  headshotInputRef: React.RefObject<HTMLInputElement>;
  /** Handle file input change — queues new files for upload */
  handleHeadshotSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Remove the headshot at this position in `allHeadshotPreviews` */
  handleRemove: (index: number) => void;
  /** Move the headshot at this position to the front: it becomes the primary */
  handleSetPrimary: (index: number) => void;
  /** Trigger the hidden file input click */
  triggerFileInput: () => void;
  /**
   * Upload any new files and return the headshot array in display order,
   * or `undefined` if nothing changed, or `null` if all headshots were cleared.
   */
  uploadHeadshotsIfNeeded: () => Promise<HeadshotEntry[] | null | undefined>;
  /** Reset headshot state to initial values. Call from the parent's reset useEffect. */
  reset: () => void;
}

/**
 * Encapsulates all headshot-related state and logic for the SpeakerFormModal:
 * one ordered list of saved headshots and picked files, so any of them can be
 * made the primary (the first, which nametags show) before it is even
 * uploaded; preview URLs; and upload orchestration that saves the list in
 * that order.
 *
 * Does NOT manage its own reset timing — exposes `reset` so the parent's
 * single useEffect can coordinate all form resets together.
 */
export function useSpeakerFormHeadshots(
  speakerToEdit?: SpeakerHeadshotInput,
): SpeakerFormHeadshots {
  const datoClientStore = useDatoClient();
  const speakerHeadshots = speakerToEdit?.headshots;
  const speakerHeadshot = speakerToEdit?.headshot;

  const initialExisting = useMemo(
    () =>
      speakerHeadshots ||
      (speakerHeadshot ? [{ id: '', url: speakerHeadshot }] : []),
    [speakerHeadshots, speakerHeadshot],
  );
  const initialItems = useMemo<HeadshotItem[]>(
    () =>
      initialExisting.map(entry => ({
        kind: 'existing',
        entry,
        preview: entry.url,
      })),
    [initialExisting],
  );

  const [items, setItems] = useState<HeadshotItem[]>(initialItems);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const headshotInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setItems(initialItems);
    setIsUploadingHeadshot(false);
  }, [initialItems]);

  const handleHeadshotSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) {
        const added = Array.from(files).map(
          (file): HeadshotItem => ({
            kind: 'new',
            file,
            preview: URL.createObjectURL(file),
          }),
        );
        setItems(prev => [...prev, ...added]);
      }
      // Reset input so the same file can be selected again
      if (headshotInputRef.current) {
        headshotInputRef.current.value = '';
      }
    },
    [],
  );

  const handleRemove = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSetPrimary = useCallback((index: number) => {
    setItems(prev =>
      index <= 0 || index >= prev.length
        ? prev
        : [prev[index], ...prev.filter((_, i) => i !== index)],
    );
  }, []);

  const triggerFileInput = useCallback(() => {
    headshotInputRef.current?.click();
  }, []);

  const allHeadshotPreviews = useMemo(
    () => items.map(item => item.preview),
    [items],
  );

  const uploadHeadshotsIfNeeded = useCallback(async (): Promise<
    HeadshotEntry[] | null | undefined
  > => {
    const speakerRepo = datoClientStore?.speakerRepository;

    // A change is a picked file, or saved headshots removed or reordered.
    const existing = items.flatMap(item =>
      item.kind === 'existing' ? [item.entry] : [],
    );
    const hasNewFiles = items.some(item => item.kind === 'new');
    const existingChanged =
      existing.length !== initialExisting.length ||
      existing.some((h, i) => h.url !== initialExisting[i]?.url) ||
      // A picked file made primary moves ahead of the saved ones.
      (hasNewFiles && items[0]?.kind === 'new' && existing.length > 0);

    if (!existingChanged && !hasNewFiles) return undefined; // no change

    // If all headshots removed and no new files, clear the gallery
    if (items.length === 0) return null;

    setIsUploadingHeadshot(true);
    try {
      // Upload new files in parallel, then put every headshot back in the
      // order shown, so the primary is saved first.
      const uploaded = new Map<File, HeadshotEntry | null>();
      if (hasNewFiles && speakerRepo) {
        const files = items.flatMap(item =>
          item.kind === 'new' ? [item.file] : [],
        );
        const uploads = await Promise.all(
          files.map(file => speakerRepo.uploadHeadshot(file)),
        );
        files.forEach((file, i) => uploaded.set(file, uploads[i] ?? null));
      }
      const result: HeadshotEntry[] = items.flatMap(item => {
        if (item.kind === 'existing') return item.entry.id ? [item.entry] : [];
        const entry = uploaded.get(item.file);
        return entry ? [entry] : [];
      });

      return result.length > 0
        ? result
        : existing.length > 0
          ? undefined
          : null;
    } catch (err) {
      logger.error('Failed to upload headshots', extractErrorInfo(err));
      return undefined;
    } finally {
      setIsUploadingHeadshot(false);
    }
  }, [datoClientStore, items, initialExisting]);

  return {
    allHeadshotPreviews,
    isUploadingHeadshot,
    headshotInputRef,
    handleHeadshotSelect,
    handleRemove,
    handleSetPrimary,
    triggerFileInput,
    uploadHeadshotsIfNeeded,
    reset,
  };
}
